import '@components/Common/CommonStyling.scss';
import './ScenarioManagement.scss';

import { ColDef, GridOptions, RowDataUpdatedEvent } from '@amzn/gft-ag-grid-enterprise';
import { AurigaGrid, AurigaGridRef } from '@components/Common/AurigaGrid/AurigaGrid';
import AurigaLoader from '@components/Common/AurigaLoader';
import PageSequenceCellRenderer from '@components/Common/CellRenderers/PageSequenceCellRenderer';
import Notification from '@components/Common/Notification';
import { globalGridDefaults } from '@constants/agGrid';
import { useAurigaContext } from '@context/AurigaContext';
import { useDeckContext } from '@context/DeckContext';
import { useScenarioManagementContext } from '@context/ScenarioManagementContext';
import { PermissionLevel, useUserContext } from '@context/UserContext';
import AsyncSaveButton from '@deckManagement//components/Common/AsyncSaveButton/AsyncSaveButton';
import LockDeckButton from '@deckManagement/components/Common/LockDeckButton/LockDeckButton';
import ScenarioManagementHeaderControls from '@deckManagement/components/ScenarioManagement/ScenarioManagementHeaderControls';
import useRecordLoadComplete from '@hooks/useRecordLoadComplete';
import { InfoOutlined } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import DownloadIcon from '@mui/icons-material/Download';
import { AlertColor, Button, IconButton, Stack } from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { PublishStatus, ScheduleType } from '@services/api/deckManagement/graphql/schema/API';
import { formatTimestampToUTC } from '@utils/dateHelpers';
import isEmpty from 'lodash/isEmpty';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PageSequenceCellRendererProps } from 'types/Common.types';
import { ScenarioManagementRowModel } from 'types/ScenarioManagement.types';

import CheckboxCellRenderer from './CellRenderers/CheckboxCellRenderer';
import RowActionsCellRenderer from './CellRenderers/RowActionsCellRenderer';
import SelectboxCellRenderer from './CellRenderers/SelectboxCellRenderer';

const PAGE_GRAIN_OPTIONS = ['Summary', 'MTD', 'QTD', 'YTD'];

// PublishStatus groups for different dialog states
const DIALOG_TRIGGER_STATUSES = [
  PublishStatus.DECK_UPDATE_COMPLETED,
  PublishStatus.KPI_UPLOAD_FAILED,
  PublishStatus.KPI_CALCULATION_FAILED,
  PublishStatus.DECK_UPDATE_FAILED,
  PublishStatus.PUBLISH_STARTED,
  PublishStatus.PUBLISH_IN_PROGRESS,
  PublishStatus.KPI_UPLOAD_COMPLETED,
  PublishStatus.KPI_CALCULATION_COMPLETED,
];

const FAILED_STATUSES = [
  PublishStatus.KPI_UPLOAD_FAILED,
  PublishStatus.KPI_CALCULATION_FAILED,
  PublishStatus.DECK_UPDATE_FAILED,
];

const IN_PROGRESS_STATUSES = [
  PublishStatus.PUBLISH_STARTED,
  PublishStatus.PUBLISH_IN_PROGRESS,
  PublishStatus.KPI_UPLOAD_COMPLETED,
  PublishStatus.KPI_CALCULATION_COMPLETED,
];

const ScenarioManagement = () => {
  const { showAlert, dismissAlert } = useAurigaContext();
  const { hasPermissionLevel } = useUserContext();
  const { deckLockStatus } = useDeckContext();

  // Track alert IDs for dismissing when status changes
  const systemRunAlertId = useRef<string | null>(null);
  const publishAlertId = useRef<string | null>(null);
  const {
    deckCycleOptions,
    pageOptions,
    hasModifiedEntries,
    filteredScenarioEntries: scenarioManagementEntries,
    handleNewEntry,
    handleDeleteEntry,
    handleModifyEntry,
    handleSaveChanges,
    handleDiscardChanges,
    isSaving,
    isLoading,
    scenarioRowFilters,
    setScenarioRowFilters,
    notification,
    systemRunInProgress,
    publishData,
    publishScenarioEntry,
    isMonthApplicable,
  } = useScenarioManagementContext();
  useRecordLoadComplete(!isLoading);

  const gridRef = useRef<AurigaGridRef>(null); // Grid ref for API access

  const [newRowAdded, setNewRowAdded] = useState<boolean>(false);
  const [isRowDragging, setIsRowDragging] = useState<boolean>(false);
  const [systemRunAlertOpen, setSystemRunAlertOpen] = useState<boolean>(true);
  const [publishAlertOpen, setPublishAlertOpen] = useState<boolean>(false);

  const canReorderApprovedSchedule = useMemo(
    () => hasPermissionLevel(PermissionLevel.DEV_TEAM_ADMIN),
    [hasPermissionLevel],
  );

  // Reset alert state when systemRunInProgress changes to true
  useEffect(() => {
    if (systemRunInProgress) {
      setSystemRunAlertOpen(true);
    }
  }, [systemRunInProgress, systemRunAlertOpen]);

  /**
   * Handle publish status notifications
   */
  useEffect(() => {
    const shouldShowDialog = DIALOG_TRIGGER_STATUSES.includes(publishData?.publishStatus as PublishStatus);
    setPublishAlertOpen(shouldShowDialog);
  }, [publishData?.publishStatus]);

  /**
   * Determines if the grid is editable based on the schedule type.
   */
  const isEditable = useMemo(
    () => scenarioRowFilters.scheduleType === ScheduleType.MODIFIED,
    [scenarioRowFilters.scheduleType],
  );

  /**
   * Determines if row reordering is enabled based on the lock status and permission level.
   */
  const isRowDraggingEnabled = useMemo(
    () =>
      deckLockStatus.isLockedByCurrentUser &&
      (isEditable || (scenarioRowFilters.scheduleType === ScheduleType.APPROVED && canReorderApprovedSchedule)),
    [canReorderApprovedSchedule, isEditable, deckLockStatus.isLockedByCurrentUser, scenarioRowFilters.scheduleType],
  );

  /**
   * Handler for New Row Action
   */
  const handleNewRowAction = useCallback(() => {
    handleNewEntry();
    setNewRowAdded(true);
  }, [handleNewEntry]);

  /**
   * Handler for RowDataUpdated events.
   * Currently used for scrolling to bottom when new row is inserted.
   */
  const handleRowDataUpdated = useCallback(
    (event: RowDataUpdatedEvent) => {
      const gridApi = event.api;

      // Handle scrolling to bottom (where the new entry was inserted)
      if (newRowAdded && gridApi) {
        gridApi.ensureIndexVisible(scenarioManagementEntries.length - 1);
        setNewRowAdded(false);
      }
    },
    [newRowAdded, scenarioManagementEntries.length],
  );

  /**
   * Handles the Discard Action, which resets rowData and calls handleDiscardChanges() from the context.
   */
  const handleDiscardAction = useCallback(() => {
    if (handleDiscardChanges) {
      if (gridRef.current) {
        // Empty the rowData to clear unsaved row ordering.
        // When the handleDiscardChanges() completes, it will restore the data with the original row ordering.
        gridRef.current.api.setGridOption('rowData', []);
      }
      handleDiscardChanges();
    }
  }, [handleDiscardChanges]);

  /**
   * AGGrid Column Definitions
   */
  const columnDefs = useMemo(
    () =>
      [
        {
          field: 'sno',
          headerName: 'Page',
          initialFlex: 1,
          headerClass: 'ag-header-cell',
          resizable: true,
          cellRenderer: PageSequenceCellRenderer,
          cellRendererParams: {
            isRowDragging,
            readonly: !isRowDraggingEnabled, // <-- When true, just reads directly from field (no updates).
            // onUpdate is called when AGGrid RowIndex is updated, allowing you to modify your row data.
            onUpdate: (node, newPageNumber) => handleModifyEntry(node.data!.metadata.internalId, 'sno', newPageNumber),
          } as PageSequenceCellRendererProps<ScenarioManagementRowModel>,
          rowDrag: isRowDraggingEnabled,
        },
        {
          field: 'page_name',
          headerName: 'Page Name',
          initialFlex: 5,
          resizable: true,
          cellRenderer: SelectboxCellRenderer,
          cellStyle: () => {
            if (isEditable) {
              return { paddingLeft: '0px', paddingRight: '0px' };
            }
            return undefined;
          },
          cellRendererParams: {
            readonly: !isEditable,
            options: pageOptions,
            onChange: handleModifyEntry,
          },
        },
        {
          field: 'page_grain',
          headerName: 'Page Grain',
          initialFlex: 2,
          resizable: true,
          editable: false,
          cellRenderer: SelectboxCellRenderer,
          cellStyle: () => {
            if (isEditable) {
              return { paddingLeft: '0px', paddingRight: '0px' };
            }
            return undefined;
          },
          cellRendererParams: {
            readonly: !isEditable,
            options: PAGE_GRAIN_OPTIONS,
            onChange: handleModifyEntry,
          },
        },
        {
          field: 'is_external_page',
          headerName: 'External Pages',
          initialFlex: 2,
          minWidth: 124,
          resizable: true,
          cellRenderer: CheckboxCellRenderer,
          cellRendererParams: {
            readonly: !isEditable,
            onChange: handleModifyEntry,
          },
        },
        // TODO: This column is not part of the mockup but it is present from the service data.
        //  To avoid confusion, I'm hiding this column until a larger discussion is had.
        // {
        //   field: 'is_supplemental',
        //   headerName: 'Supplemental Pages',
        //   initialFlex: 2,
        //   minWidth: 124,
        //   resizable: false,
        //   cellRenderer: CheckboxCellRenderer,
        //   cellRendererParams: {
        //     readonly: !isEditable,
        //     onChange: handleModifyEntry,
        //   },
        // },
        {
          headerName: '',
          initialFlex: 1,
          minWidth: 64,
          resizable: false,
          cellRenderer: isEditable ? RowActionsCellRenderer : undefined,
          cellRendererParams: {
            onDelete: handleDeleteEntry,
          },
        },
      ] as ColDef<ScenarioManagementRowModel>[],
    [handleDeleteEntry, handleModifyEntry, isEditable, isRowDraggingEnabled, isRowDragging, pageOptions],
  );

  /**
   * AGGrid Default Column Definitions
   */
  const defaultColDef = useMemo(() => {
    return {
      resizable: false,
      sortable: false,
    };
  }, []);

  /**
   * AGGrid Grid Options
   */
  const gridOptions: GridOptions<ScenarioManagementRowModel> = useMemo(() => {
    return {
      rowClassRules: {
        'modified-row': (params) => (params.data?.metadata.isModified || params.data?.metadata.isNewRow) ?? false,
        'hidden-row': (params) => params.data?.is_blankpage ?? false,
      },
      getRowId: (params) => params.data.metadata.internalId,
    };
  }, []);

  /**
   * Renders the Save Button
   */
  const renderSaveButton = useCallback(
    () => (
      <AsyncSaveButton
        inProgress={isSaving}
        inProgressContent='Saving...'
        buttonProps={{
          classes: {
            root: 'save-button',
          },
          variant: 'contained',
          color: 'primary',
          disabled: !hasModifiedEntries || isSaving || deckLockStatus.isEditingDisabled,
          onClick: handleSaveChanges,
        }}
      >
        <Typography variant='inherit'>Save Changes</Typography>
      </AsyncSaveButton>
    ),
    [handleSaveChanges, hasModifiedEntries, isSaving, deckLockStatus.isEditingDisabled],
  );

  /**
   * Renders the Discard Button
   */
  const renderDiscardButton = useCallback(
    () => (
      <Button
        variant='outlined'
        color='primary'
        onClick={handleDiscardAction}
        disabled={!hasModifiedEntries || isSaving}
      >
        Discard
      </Button>
    ),
    [handleDiscardAction, hasModifiedEntries, isSaving],
  );

  const isPublishCompleted = publishData?.publishStatus === PublishStatus.DECK_UPDATE_COMPLETED;
  const isPublishFailed = FAILED_STATUSES.includes(publishData?.publishStatus as PublishStatus);

  const isPublishInProgress = IN_PROGRESS_STATUSES.includes(publishData?.publishStatus as PublishStatus);

  /**
   * Renders the Publish Button
   */
  const renderPublishButton = useCallback(
    () => (
      <AsyncSaveButton
        inProgress={isPublishInProgress}
        inProgressContent='Publishing...'
        buttonProps={{
          classes: {
            root: 'sm-publish-button',
          },
          variant: 'contained',
          color: 'primary',
          disabled: hasModifiedEntries || isPublishInProgress || deckLockStatus.isEditingDisabled,
          onClick: () => {
            setSystemRunAlertOpen(true);
            publishScenarioEntry();
          },
        }}
      >
        Publish
      </AsyncSaveButton>
    ),
    [hasModifiedEntries, isPublishInProgress, deckLockStatus.isEditingDisabled, publishScenarioEntry],
  );

  /**
   * Renders the controls for Approved Schedule Type.
   */
  const renderApprovedScheduleControls = useCallback(() => {
    return (
      <Box className='schedule-controls-group'>
        {canReorderApprovedSchedule && renderDiscardButton()}
        {canReorderApprovedSchedule && renderSaveButton()}
        {renderPublishButton()}
      </Box>
    );
  }, [canReorderApprovedSchedule, renderDiscardButton, renderPublishButton, renderSaveButton]);

  /**
   * Renders the controls for Modified Schedule Type.
   */
  const renderModifiedScheduleControls = useCallback(() => {
    return (
      <Box className='schedule-controls-group'>
        <Button
          className='new-row-button'
          variant='text'
          color='primary'
          onClick={handleNewRowAction}
          startIcon={<AddIcon />}
          disabled={isEmpty(pageOptions) || isEmpty(deckCycleOptions) || deckLockStatus.isEditingDisabled}
        >
          New Row
        </Button>
        {renderDiscardButton()}
        {renderSaveButton()}
        {renderPublishButton()}
      </Box>
    );
  }, [
    handleNewRowAction,
    pageOptions,
    deckCycleOptions,
    deckLockStatus.isEditingDisabled,
    renderDiscardButton,
    renderSaveButton,
    renderPublishButton,
  ]);

  /**
   * Shows an alert when a system run is in progress
   */
  useEffect(() => {
    // Dismiss previous alert if it exists
    if (systemRunAlertId.current) {
      dismissAlert(systemRunAlertId.current);
      systemRunAlertId.current = null;
    }

    if (systemRunInProgress && systemRunAlertOpen) {
      const alertId = showAlert({
        message: 'System Run In Progress',
        type: 'info',
        variant: 'filled',
        visible: true,
        alertRoutes: ['scenario-management'],
        dismissible: true,
        onClose: () => {
          setSystemRunAlertOpen(false);
        },
        icon: <InfoOutlined fontSize='inherit' fontWeight='800' />,
      });

      systemRunAlertId.current = alertId;
    }
  }, [systemRunInProgress, systemRunAlertOpen, showAlert, dismissAlert]);

  /**
   * Displays an alert based on the current publish status
   * Shows different alert types (info/success/error) depending on publish state
   */
  useEffect(() => {
    // Dismiss previous alert if it exists
    if (publishAlertId.current) {
      dismissAlert(publishAlertId.current);
      publishAlertId.current = null;
    }

    if (publishData?.publishStatus && publishAlertOpen) {
      let alertColor: AlertColor = 'info';
      // Note: publishTimestamp is used as the display date/time here, as it indicates the current date/time of when the active publish began
      // TODO: Decide if we want to display 'lastPublishDate' elsewhere, to show when the last successful completion of publish on this Deck occurred
      let message = `Manual Approved Schedule Publish in progress since: ${formatTimestampToUTC(publishData?.publishTimestamp || '')}, by: ${publishData?.publishUser || 'Unknown User'}. Last publish step: ${publishData?.publishStatus}`;

      if (isPublishCompleted) {
        alertColor = 'success';
        message = 'Manual Approved Schedule Publish successful';
      } else if (isPublishFailed) {
        alertColor = 'error';
        message = 'Manual Approved Schedule Publish failed. Please review and try again.';
      }

      const alertId = showAlert({
        message,
        type: alertColor,
        variant: 'filled',
        visible: true,
        alertRoutes: ['scenario-management'],
        dismissible: true,
        onClose: () => {
          setPublishAlertOpen(false);
        },
        icon: <InfoOutlined fontSize='inherit' fontWeight='800' />,
      });

      publishAlertId.current = alertId;
    }
  }, [
    publishData?.publishStatus,
    publishAlertOpen,
    isPublishCompleted,
    isPublishFailed,
    showAlert,
    dismissAlert,
    publishData?.publishTimestamp,
    publishData?.publishUser,
  ]);

  const renderHeaderControls = useCallback(() => {
    return (
      <ScenarioManagementHeaderControls
        scenarioRowFilters={scenarioRowFilters}
        setScenarioRowFilters={setScenarioRowFilters}
        deckCycleOptions={deckCycleOptions}
        isMonthApplicable={isMonthApplicable}
      />
    );
  }, [deckCycleOptions, scenarioRowFilters, setScenarioRowFilters, isMonthApplicable]);

  /**
   * Renders the main AGGrid component
   */
  const renderContent = useCallback(() => {
    if (isLoading) {
      return <AurigaLoader loaderText='Loading Scenario Management' />;
    }

    return (
      <Box className='grid-container row-highlighting'>
        <AurigaGrid
          {...globalGridDefaults}
          ref={gridRef}
          rowData={scenarioManagementEntries}
          columnDefs={columnDefs}
          gridOptions={gridOptions}
          defaultColDef={defaultColDef}
          stopEditingWhenCellsLoseFocus
          suppressScrollOnNewData
          onRowDataUpdated={handleRowDataUpdated}
          suppressMaintainUnsortedOrder
          rowDragManaged
          rowDragText={(params) => {
            // Show "Page_Name | Page_Grain" data when dragging
            return `Pg ${params.rowNode?.data.page_name} | ${params.rowNode?.data.page_grain}`;
          }}
          onRowDragEnter={() => setIsRowDragging(true)}
          onRowDragEnd={() => setIsRowDragging(false)}
        />
      </Box>
    );
  }, [columnDefs, defaultColDef, gridOptions, handleRowDataUpdated, isLoading, scenarioManagementEntries]);

  return (
    <Box className='scenario-management'>
      <Box className='header-container'>
        <Box className='left-section'>
          <Stack className='scenario-management__header-stack'>
            <Typography variant='h5'>Scenario Management</Typography>
            {renderHeaderControls()}
          </Stack>
        </Box>
        <Box className='button-group padding-x-none padding-top-none'>
          <IconButton onClick={() => {}} aria-label='download' className='download-button'>
            <DownloadIcon />
          </IconButton>
          {scenarioRowFilters.scheduleType === ScheduleType.MODIFIED && renderModifiedScheduleControls()}
          {scenarioRowFilters.scheduleType === ScheduleType.APPROVED && renderApprovedScheduleControls()}

          {/* Lock/Release Deck Button */}
          <Box className='lock-deck-section'>
            <LockDeckButton />
          </Box>
        </Box>
      </Box>
      {renderContent()}
      {notification && <Notification {...notification} />}
    </Box>
  );
};

export default ScenarioManagement;
